// src/components/Explore/FilterBar.jsx
// Smart filter bar for the Explore mode with natural language parsing

import React, { useState, useCallback, useMemo } from 'react';
import { 
  FaFutbol, 
  FaChild, 
  FaDollarSign, 
  FaCalendarAlt, 
  FaSearch,
  FaTimes,
  FaCheckSquare,
  FaSyncAlt,
  FaMagic
} from 'react-icons/fa';
import FilterDropdown from './filters/FilterDropdown';
import FilterPill from './filters/FilterPill';
import { 
  AGE_GROUP_OPTIONS, 
  PRICE_RANGE_OPTIONS, 
  START_DATE_OPTIONS 
} from '../../constants/exploreFilters';
import { useSports } from '../../hooks/useSports';
import { parseSearchQuery } from '../../utils/searchParser';
import styles from './FilterBar.module.css';

const FilterBar = ({
  filters,
  updateFilter,
  updateFilters,
  clearFilter,
  resetFilters,
  activeFiltersCount,
  totalStats,
  isLoading,
  onRefresh
}) => {
  const [inputValue, setInputValue] = useState(filters.searchQuery || '');
  const [showSmartHint, setShowSmartHint] = useState(false);
  const [parsedPreview, setParsedPreview] = useState(null);
  
  // Use dynamic sports from API
  const { sportsOptionsWithAll: SPORTS_OPTIONS, loading: sportsLoading } = useSports();

  // Parse input and show preview
  const handleInputChange = useCallback((value) => {
    setInputValue(value);
    
    if (value.length > 3) {
      const { filters: parsed, remainingQuery } = parseSearchQuery(value);
      const hasFilters = Object.keys(parsed).length > 0;
      
      if (hasFilters) {
        setParsedPreview(parsed);
        setShowSmartHint(true);
      } else {
        setParsedPreview(null);
        setShowSmartHint(false);
      }
    } else {
      setParsedPreview(null);
      setShowSmartHint(false);
    }
  }, []);

  // Apply smart search (parse and apply filters)
  const applySmartSearch = useCallback(() => {
    if (!inputValue.trim()) return;

    const { filters: parsed, remainingQuery } = parseSearchQuery(inputValue);
    
    // Apply all parsed filters at once
    if (Object.keys(parsed).length > 0 && updateFilters) {
      updateFilters({
        ...parsed,
        searchQuery: remainingQuery
      });
      setInputValue(remainingQuery);
    } else {
      // No filters found, just use as regular search
      updateFilter('searchQuery', inputValue);
    }
    
    setParsedPreview(null);
    setShowSmartHint(false);
  }, [inputValue, updateFilter, updateFilters]);

  // Handle Enter key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applySmartSearch();
    }
  }, [applySmartSearch]);

  // Clear input
  const handleClear = useCallback(() => {
    setInputValue('');
    updateFilter('searchQuery', '');
    setParsedPreview(null);
    setShowSmartHint(false);
  }, [updateFilter]);

  // Get label for filter preview
  const getFilterLabel = (key, value) => {
    switch (key) {
      case 'sport':
        return SPORTS_OPTIONS.find(o => o.value === value)?.label || value;
      case 'ageGroup':
        return AGE_GROUP_OPTIONS.find(o => o.value === value)?.label || value;
      case 'priceRange':
        return PRICE_RANGE_OPTIONS.find(o => o.value === value)?.label || value;
      case 'startDate':
        return START_DATE_OPTIONS.find(o => o.value === value)?.label || value;
      case 'hasOpenings':
        return value ? 'Has Openings' : 'All';
      default:
        return value;
    }
  };

  // Get icon for filter preview
  const getFilterIcon = (key) => {
    switch (key) {
      case 'sport': return '⚽';
      case 'ageGroup': return '👶';
      case 'priceRange': return '💰';
      case 'startDate': return '📅';
      case 'hasOpenings': return '✅';
      default: return '🔍';
    }
  };

  // Get active filter pills
  const getActiveFilters = () => {
    const active = [];

    if (filters.sport && filters.sport !== 'All') {
      const option = SPORTS_OPTIONS.find(o => o.value === filters.sport);
      active.push({
        key: 'sport',
        label: option?.label || filters.sport,
        icon: FaFutbol
      });
    }

    if (filters.ageGroup && filters.ageGroup !== 'all') {
      const option = AGE_GROUP_OPTIONS.find(o => o.value === filters.ageGroup);
      active.push({
        key: 'ageGroup',
        label: option?.label || filters.ageGroup,
        icon: FaChild
      });
    }

    if (filters.priceRange && filters.priceRange !== 'all') {
      const option = PRICE_RANGE_OPTIONS.find(o => o.value === filters.priceRange);
      active.push({
        key: 'priceRange',
        label: option?.label || filters.priceRange,
        icon: FaDollarSign
      });
    }

    if (filters.startDate && filters.startDate !== 'all') {
      const option = START_DATE_OPTIONS.find(o => o.value === filters.startDate);
      active.push({
        key: 'startDate',
        label: option?.label || filters.startDate,
        icon: FaCalendarAlt
      });
    }

    if (filters.hasOpenings) {
      active.push({
        key: 'hasOpenings',
        label: 'Has Openings',
        icon: FaCheckSquare
      });
    }

    return active;
  };

  const activeFilters = getActiveFilters();

  return (
    <div className={styles.filterBar}>
      {/* Smart Search Input */}
      <div className={styles.searchWrapper}>
        <FaSearch className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Try: 'soccer for kids 8-10' or 'basketball under $100'..."
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.searchInput}
        />
        {inputValue && (
          <button
            type="button"
            className={styles.clearSearch}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
        {parsedPreview && Object.keys(parsedPreview).length > 0 && (
          <button
            type="button"
            className={styles.smartSearchButton}
            onClick={applySmartSearch}
            title="Apply smart filters"
          >
            <FaMagic />
          </button>
        )}
      </div>

      {/* Smart Search Preview */}
      {showSmartHint && parsedPreview && Object.keys(parsedPreview).length > 0 && (
        <div className={styles.smartPreview}>
          <span className={styles.smartPreviewLabel}>
            <FaMagic className={styles.smartIcon} />
            Detected filters:
          </span>
          <div className={styles.smartPreviewPills}>
            {Object.entries(parsedPreview).map(([key, value]) => (
              <span key={key} className={styles.smartPreviewPill}>
                {getFilterIcon(key)} {getFilterLabel(key, value)}
              </span>
            ))}
          </div>
          <button
            type="button"
            className={styles.applySmartButton}
            onClick={applySmartSearch}
          >
            Apply
          </button>
        </div>
      )}

      {/* Filter Dropdowns */}
      <div className={styles.filtersRow}>
        <FilterDropdown
          label="Sport"
          icon={FaFutbol}
          value={filters.sport}
          options={SPORTS_OPTIONS}
          onChange={(value) => updateFilter('sport', value)}
        />

        <FilterDropdown
          label="Age"
          icon={FaChild}
          value={filters.ageGroup}
          options={AGE_GROUP_OPTIONS}
          onChange={(value) => updateFilter('ageGroup', value)}
        />

        <FilterDropdown
          label="Price"
          icon={FaDollarSign}
          value={filters.priceRange}
          options={PRICE_RANGE_OPTIONS}
          onChange={(value) => updateFilter('priceRange', value)}
        />

        <FilterDropdown
          label="Starts"
          icon={FaCalendarAlt}
          value={filters.startDate}
          options={START_DATE_OPTIONS}
          onChange={(value) => updateFilter('startDate', value)}
        />

        {/* Has Openings Toggle */}
        <button
          type="button"
          className={`${styles.openingsToggle} ${filters.hasOpenings ? styles.active : ''}`}
          onClick={() => updateFilter('hasOpenings', !filters.hasOpenings)}
        >
          <FaCheckSquare className={styles.toggleIcon} />
          <span>Has Openings</span>
        </button>
      </div>

      {/* Active Filters Pills */}
      {activeFilters.length > 0 && (
        <div className={styles.activePillsRow}>
          <span className={styles.activeLabel}>Active:</span>
          <div className={styles.pillsContainer}>
            {activeFilters.map((filter) => (
              <FilterPill
                key={filter.key}
                label={filter.label}
                icon={filter.icon}
                onRemove={() => clearFilter(filter.key)}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.clearAllButton}
            onClick={resetFilters}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Results Count */}
      <div className={styles.resultsInfo}>
        {isLoading ? (
          <span className={styles.loadingText}>Searching...</span>
        ) : (
          <span className={styles.resultsText}>
            {totalStats.totalClubs} club{totalStats.totalClubs !== 1 ? 's' : ''} with{' '}
            {totalStats.totalPrograms} matching program{totalStats.totalPrograms !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default FilterBar;










